import React from 'react';

/**
 * Single, reusable pagination component for the whole project.
 *
 * Canonical usage (matches the backend `pagination` response shape):
 *
 *   <Paginator
 *     pagination={pagination}                 // { current_page, last_page, per_page, total }
 *     onPageChange={(page) => setPage(page)}
 *   />
 *
 * For backwards compatibility it also accepts the individual
 * `page` / `rows` / `totalRecords` props and a `setPage` callback.
 */
const Paginator = ({
  pagination,
  page,
  rows,
  totalRecords,
  onPageChange,
  setPage,
}) => {
  const currentPage = Number(pagination?.current_page ?? page ?? 1);
  const perPage = Number(pagination?.per_page ?? rows ?? 0);
  const total = Number(pagination?.total ?? totalRecords ?? 0);
  const totalPages = Number(
    pagination?.last_page ?? (perPage ? Math.ceil(total / perPage) : 0)
  );

  const changePage = onPageChange || setPage;

  // Nothing to paginate.
  if (!totalPages || totalPages <= 1) {
    return null;
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      changePage?.(newPage);
    }
  };

  // Build a windowed list of pages with ellipsis, e.g. 1 … 4 5 [6] 7 8 … 20
  const getPageItems = () => {
    const delta = 2; // pages to show on each side of the current page
    const items = [];
    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    items.push(1);
    if (left > 2) items.push('left-ellipsis');
    for (let i = left; i <= right; i++) items.push(i);
    if (right < totalPages - 1) items.push('right-ellipsis');
    if (totalPages > 1) items.push(totalPages);

    return items;
  };

  return (
    <nav aria-label="Page navigation">
      <ul className="pagination justify-content-center">
        {/* Previous */}
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
        </li>

        {/* Page numbers */}
        {getPageItems().map((item, index) =>
          typeof item === 'string' ? (
            <li key={item} className="page-item disabled">
              <span className="page-link">…</span>
            </li>
          ) : (
            <li
              key={item}
              className={`page-item ${currentPage === item ? 'active' : ''}`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(item)}
              >
                {item}
              </button>
            </li>
          )
        )}

        {/* Next */}
        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Paginator;
